import { CommonModule } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { Component, ElementRef, Input, OnDestroy, OnInit, signal } from '@angular/core'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { IonCheckbox, IonIcon, ModalController } from '@ionic/angular/standalone'
import { TranslatePipe } from '@ngx-translate/core'
import { addIcons } from 'ionicons'
import { callOutline, closeOutline, eyeOffOutline, eyeOutline, lockClosedOutline, mailOutline, personOutline } from 'ionicons/icons'
import { finalize, Subject, takeUntil } from 'rxjs'

import { environment } from '../../../environments/environment'
import { ToastService } from '../../shared/components/toast/toast.service'
import { TOAST_STATE } from '../../shared/components/toast/types/toast.type'
import { AuthService } from '../../shared/services/auth.service'
import { ClientIpService } from '../../shared/services/client-ip.service'
import { DomainService } from '../../shared/services/domain.service'
import { WhiteLabelService } from '../../shared/services/white-label.service'
import { LoginResponse } from '../../shared/types/auth.type'
import { ApiResponse } from '../../shared/types/response.type'
import { LoginRequest } from './types/login.type'

export type LoginViewMode = 'login' | 'register'

interface Turnstile {
	reset: (widgetId: string) => void
	render: (
		container: HTMLElement | string,
		options: {
			sitekey: string
			theme?: string
			size?: string
			callback?: (token: string) => void
			'expired-callback'?: () => void
			'error-callback'?: () => void
		},
	) => string
	remove: (widgetId: string) => void
}

@Component({
	selector: 'app-login',
	templateUrl: './login.page.html',
	styleUrl: './login.page.scss',
	standalone: true,
	imports: [CommonModule, FormsModule, IonIcon, ReactiveFormsModule, TranslatePipe, IonCheckbox],
})
export class LoginPage implements OnInit, OnDestroy {
	@Input() initialMode: LoginViewMode = 'login'

	isPasswordVisible = signal<boolean>(false)

	turnstileToken: string | null = null

	turnstileWidgetId: string | null = null

	isRegisterPasswordVisible = signal<boolean>(false)

	isRegisterConfirmPasswordVisible = signal<boolean>(false)

	isSubmitted = signal<boolean>(false)

	isRegisterSubmitted = signal<boolean>(false)

	isSubmitting = signal<boolean>(false)

	isRegisterSubmitting = signal<boolean>(false)

	errorMessage = signal<string | null>(null)

	registerErrorMessage = signal<string | null>(null)

	isRegistering = signal<boolean>(false)

	private readonly destroy$ = new Subject<void>()

	loginForm = new FormGroup({
		username: new FormControl('', [Validators.required]),
		password: new FormControl('', [Validators.required]),
		rememberMe: new FormControl<boolean>(false),
	})

	registerForm = new FormGroup({
		fullName: new FormControl('', [Validators.required]),
		username: new FormControl('', [Validators.required]),
		phoneNumber: new FormControl('', [Validators.required]),
		email: new FormControl('', [Validators.required, Validators.email]),
		password: new FormControl('', [Validators.required, Validators.minLength(6)]),
		confirmPassword: new FormControl('', [Validators.required]),
		rememberMe: new FormControl<boolean>(false),
	})

	constructor(
		private readonly router: Router,
		private readonly authService: AuthService,
		private readonly toastService: ToastService,
		protected readonly domainService: DomainService,
		protected readonly whiteLabelService: WhiteLabelService,
		private readonly clientIpService: ClientIpService,
		private readonly elRef: ElementRef,
		private readonly modalController: ModalController,
	) {
		addIcons({
			eyeOutline,
			eyeOffOutline,
			lockClosedOutline,
			personOutline,
			callOutline,
			mailOutline,
			closeOutline,
		})
	}

	ngOnInit() {
		this.isRegistering.set(this.initialMode === 'register')
		this.setupRememberMe()

		if (this.whiteLabelService.isTurnstileEnabled()) {
			setTimeout(() => {
				this.loadTurnstileScript()
			}, 0)
		}
	}

	/**
	 * Configures the 'Remember Me' feature for the login form. If saved credentials are found,
	 * it pre-fills the form fields with the stored username and password and sets the rememberMe toggle to true.
	 *
	 * @return {void} Does not return a value.
	 */
	setupRememberMe(): void {
		const rememberMe = this.getRememberMe()
		if (rememberMe) {
			this.loginForm.patchValue({
				username: rememberMe.username,
				password: rememberMe.password,
				rememberMe: true,
			})
		}
	}

	/**
	 * Toggle password visibility
	 */
	toggleShowPassword() {
		this.isPasswordVisible.update((currentValue) => !currentValue)
	}

	toggleShowRegisterPassword() {
		this.isRegisterPasswordVisible.update((currentValue) => !currentValue)
	}

	toggleShowRegisterConfirmPassword() {
		this.isRegisterConfirmPasswordVisible.update((currentValue) => !currentValue)
	}

	getRememberMe() {
		try {
			const rememberMeFromLocalStorage = localStorage.getItem('rememberMe')
			if (rememberMeFromLocalStorage) {
				const rememberMe = JSON.parse(rememberMeFromLocalStorage) as { y: string; z: string }
				const username = this.authService.decrypt(rememberMe.y)
				const password = this.authService.decrypt(rememberMe.z)

				return { username, password }
			}

			return undefined
		} catch (e) {
			console.error(e)
			return undefined
		}
	}

	setRememberMe(username: string, password: string) {
		const encryptedUsername = this.authService.encrypt(username)
		const encryptedPassword = this.authService.encrypt(password)
		localStorage.setItem('rememberMe', JSON.stringify({ y: encryptedUsername, z: encryptedPassword }))
	}

	clearRememberMe() {
		localStorage.removeItem('rememberMe')
	}

	closeModal() {
		this.modalController.dismiss()
	}

	private parseErrorMessage(error: HttpErrorResponse | Error): string {
		if (error instanceof HttpErrorResponse && error.error?.error && typeof error.error.error === 'string') {
			const clean = error.error.error
				.replace(/^db error:\s*/i, '')
				.split('DETAIL:')[0]
				.trim()
			return clean.toUpperCase().startsWith('ERROR:') ? clean : `ERROR: ${clean}`
		}
		return 'ERROR: Unable to process request. Please try again.'
	}

	/**
	 * Submit the login form data
	 */
	login(loginParams?: { username: string; password: string }) {
		let loginPayload: Partial<LoginRequest>
		if (loginParams) {
			loginPayload = { ...loginParams }
		} else {
			this.isSubmitted.set(true)
			loginPayload = {
				username: this.loginForm.value.username,
				password: this.loginForm.value.password,
			}
		}

		if (loginParams || this.loginForm.valid) {
			if (this.whiteLabelService.isTurnstileEnabled()) {
				if (!loginParams && !this.turnstileToken) {
					this.toastService.showToast(TOAST_STATE.DANGER, 'Please complete the security check.')
					return
				}
			}

			if (this.whiteLabelService.isTurnstileEnabled() && this.turnstileToken) {
				loginPayload.securityToken = this.turnstileToken
			}

			this.isSubmitting.set(true)
			this.errorMessage.set(null)
			this.authService
				.login(loginPayload)
				.pipe(
					finalize(() => this.isSubmitting.set(false)),
					takeUntil(this.destroy$),
				)
				.subscribe({
					next: (loginResponse) => {
						void this.handleLoginResponse(loginResponse, loginParams)
					},
					error: (error: HttpErrorResponse | Error) => {
						this.errorMessage.set(this.parseErrorMessage(error))
						const turnstile = (window as unknown as { turnstile?: Turnstile }).turnstile
						if (this.whiteLabelService.isTurnstileEnabled() && this.turnstileWidgetId && turnstile) {
							turnstile.reset(this.turnstileWidgetId)
							this.turnstileToken = null
						}
					},
				})
		}
	}

	private async handleLoginResponse(loginResponse: LoginResponse, loginParams?: { username: string; password: string }): Promise<void> {
		if (this.loginForm.value && this.loginForm.value.rememberMe === true) {
			this.setRememberMe(this.loginForm.value.username!, this.loginForm.value.password!)
		} else if (this.loginForm.value.rememberMe === false) {
			this.clearRememberMe()
		}
		this.authService.setLoginResponse = loginResponse

		if (loginParams === undefined && loginResponse.user_fields.IS_2FA_ENABLED === 'true') {
			await this.modalController.dismiss()
			await this.router.navigate(['two-factor-auth', `${loginResponse.user_fields.IS_2FA_VERIFIED === 'true' ? 'verify' : 'setup'}`], {
				queryParams: { 'first-time': loginResponse.user_fields.IS_2FA_VERIFIED === 'false' },
			})
		} else {
			this.authService.setUserDataAfterLogin(loginResponse)
			await this.modalController.dismiss()
			window.location.reload()
		}
	}

	/**
	 * Submit the registration form data
	 */
	async register() {
		this.isRegisterSubmitted.set(true)
		this.registerErrorMessage.set(null)

		if (this.registerForm.value.password !== this.registerForm.value.confirmPassword) {
			this.registerErrorMessage.set('Passwords do not match')
			return
		}

		if (this.registerForm.valid) {
			this.isRegisterSubmitting.set(true)

			const formValue = this.registerForm.value
			const nameParts = (formValue.fullName || '').trim().split(' ')
			const firstName = nameParts[0] || null
			const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : null
			const clientIp = await this.clientIpService.getClientIp()

			const payload = {
				NAME: formValue.username,
				PASSWORD: formValue.password,
				EMAIL: formValue.email,
				FIRST_NAME: firstName,
				LAST_NAME: lastName,
				PHONE: formValue.phoneNumber || null,
				COUNTRY_CODE: null,
				CITY: null,
				ADDRESS_LINE1: null,
				ADDRESS_LINE2: null,
				POSTAL_CODE: null,
				IP_ADDRESS: clientIp,
			}

			this.authService
				.register(payload)
				.pipe(
					finalize(() => this.isRegisterSubmitting.set(false)),
					takeUntil(this.destroy$),
				)
				.subscribe({
					next: (response: ApiResponse<string[] | string>) => {
						if (response?.status === 'error') {
							let msg = 'ERROR: Unable to register. Please try again.'
							if (response.result && Array.isArray(response.result) && response.result.length > 0) {
								let cleanMessage = response.result[0]
									.replace(/^db error:\s*/i, '')
									.split('DETAIL:')[0]
									.trim()
								if (!cleanMessage.toUpperCase().startsWith('ERROR:')) {
									cleanMessage = `ERROR: ${cleanMessage}`
								}
								msg = cleanMessage
							} else if (response.result && typeof response.result === 'string') {
								msg = response.result
							}
							this.registerErrorMessage.set(msg)
							return
						}

						this.toastService.showToast(TOAST_STATE.SUCCESS, 'Registration successfully submitted. Please login.')
						this.isRegistering.set(false)
						this.loginForm.patchValue({
							username: this.registerForm.value.username,
							password: this.registerForm.value.password,
						})
					},
					error: (error: HttpErrorResponse | Error) => {
						this.registerErrorMessage.set(this.parseErrorMessage(error))
					},
				})
		}
	}

	loadTurnstileScript() {
		const container = this.elRef.nativeElement.querySelector('#turnstile-container')
		if (!container) return

		const turnstile = (window as unknown as { turnstile?: Turnstile }).turnstile
		if (turnstile) {
			this.renderTurnstile(container)
			return
		}

		const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')
		if (existingScript) {
			const interval = setInterval(() => {
				const currentTurnstile = (window as unknown as { turnstile?: Turnstile }).turnstile
				if (currentTurnstile) {
					clearInterval(interval)
					this.renderTurnstile(container)
				}
			}, 100)
			return
		}

		const script = document.createElement('script')
		script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
		script.async = true
		script.defer = true
		script.onload = () => {
			this.renderTurnstile(container)
		}
		document.head.appendChild(script)
	}

	renderTurnstile(container: HTMLElement) {
		try {
			const turnstile = (window as unknown as { turnstile?: Turnstile }).turnstile
			if (turnstile) {
				container.innerHTML = ''
				this.turnstileWidgetId = turnstile.render(container, {
					sitekey: environment.turnstileSiteKey || '0x4AAAAAADYTlDBCGoTMDDIN',
					theme: 'dark',
					size: 'flexible',
					callback: (token: string) => {
						this.turnstileToken = token
					},
					'expired-callback': () => {
						this.turnstileToken = null
					},
					'error-callback': () => {
						this.turnstileToken = null
					},
				})
			}
		} catch (err) {
			console.error('Error rendering Turnstile widget:', err)
		}
	}

	ngOnDestroy() {
		this.destroy$.next()
		this.destroy$.complete()
		const turnstile = (window as unknown as { turnstile?: Turnstile }).turnstile
		if (this.turnstileWidgetId && turnstile) {
			try {
				turnstile.remove(this.turnstileWidgetId)
			} catch {
				// Ignore error on remove
			}
		}
	}
}
